import React from "react";
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

/**
 * How far back the roll cards reach.
 *
 * Separate from the grouping: grouping decides how the rolls in view are
 * bucketed, this decides which rolls are in view at all. A window of 30 days
 * grouped by week and one of 90 days grouped by week are the same chart at two
 * lengths.
 */
export type LamTimeframe = "30d" | "60d" | "90d";

export const TIMEFRAME_OPTIONS: Array<{ key: LamTimeframe; label: string; days: number }> = [
  { key: "30d", label: "30d", days: 30 },
  { key: "60d", label: "60d", days: 60 },
  { key: "90d", label: "90d", days: 90 },
];

/** Long enough to hold several weeks of shifts, short enough to still read. */
export const DEFAULT_TIMEFRAME: LamTimeframe = "30d";

export const timeframeDays = (t: LamTimeframe): number =>
  TIMEFRAME_OPTIONS.find((o) => o.key === t)?.days ??
  TIMEFRAME_OPTIONS.find((o) => o.key === DEFAULT_TIMEFRAME)!.days;

/**
 * `onChange` only fires for a real change — the group is exclusive, so clicking
 * the selected button would otherwise report null and clear the selection.
 */
const TimeframeSelector: React.FC<{
  value: LamTimeframe;
  onChange: (timeframe: LamTimeframe) => void;
}> = ({ value, onChange }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
      aria-hidden
    >
      Timeframe:
    </Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v: LamTimeframe | null) => {
        if (v && v !== value) onChange(v);
      }}
      aria-label="Timeframe"
    >
      {TIMEFRAME_OPTIONS.map((t) => (
        <ToggleButton key={t.key} value={t.key}>
          {t.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Stack>
);

export default TimeframeSelector;
